export type StudyTimestamp = Date | number | string;

export interface StudyStreak {
  days: number;
  endsToday: boolean;
}

function toLocalDayKey(value: StudyTimestamp): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function calculateStudyStreak(
  timestamps: StudyTimestamp[],
  now = new Date(),
): number {
  return getStudyStreak(timestamps, now).days;
}

export function getStudyStreak(
  timestamps: StudyTimestamp[],
  now = new Date(),
): StudyStreak {
  const activeDays = new Set(
    timestamps
      .map(toLocalDayKey)
      .filter((day): day is string => day !== null),
  );
  if (activeDays.size === 0) return { days: 0, endsToday: false };

  const cursor = startOfLocalDay(now);
  const todayKey = toLocalDayKey(cursor);
  const endsToday = Boolean(todayKey && activeDays.has(todayKey));
  if (!endsToday) {
    cursor.setDate(cursor.getDate() - 1);
  }

  let streak = 0;
  while (true) {
    const dayKey = toLocalDayKey(cursor);
    if (!dayKey || !activeDays.has(dayKey)) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return { days: streak, endsToday };
}

export function isDayInCurrentWeekStreak(
  dayIndex: number,
  currentDayIndex: number,
  streakDays: number,
  endsToday = true,
): boolean {
  const streakEndIndex = endsToday ? currentDayIndex : currentDayIndex - 1;
  if (streakDays <= 0 || dayIndex > streakEndIndex) return false;
  return dayIndex >= Math.max(0, streakEndIndex - streakDays + 1);
}
